import { Dialog, DialogContent, IconButton, Slide, Button, Box, Typography } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import {
    addNewMemberByCompanyID,
    deleteRepresentativeAndApplicantByCompany,
    getAllMembersByCompanyID,
    searchUserByCitizenId,
} from "../../../../../api/myCompany";
import Table from "../../../../../components/Table/Table";
import { ROLE_TYPE } from "../../../../../constants/constants";
import { columns, columnsCompanyApplicant, FormCompanySendInviteSchema } from "../CompanyConstants";
import DetailCompany from "./DetailCompany";
import DialogSelect from "./DialogSearch";
import ConfirmActionModal from "../../../../../components/ConfirmActionModal/ConfirmActionModal";

const INITIAL_FEEDBACK_MODAL = { open: false, variant: "info", title: "", message: "", showCancel: false, onConfirm: null };

export default function ModalListMemberByCompany({
    isModalListMembersVisible,
    setIsModalListMemberVisible,
    selectedCompany = null,
    companyID,
    detailCompanyActive = true,
    dialogSelectActive = true,
}) {
    const queryClient = useQueryClient();

    const [currentPage, setCurrentPage] = useState(1);
    const { data: usersMembers } = useQuery(
        ["allMembersData", currentPage],
        () => getAllMembersByCompanyID(selectedCompany?.id ?? companyID),
        {
            cacheTime: 0,
            staleTime: 0,
        }
    );
    const [searchUser, setSearchUser] = useState(null);
    const { rolBase } = useSelector((state) => state.authReducer);

    const [modalInviteVisible, setModalInviteVisible] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(INITIAL_FEEDBACK_MODAL);
    const mutationDeleteApplicant = useMutation(deleteRepresentativeAndApplicantByCompany);
    const mutationSearchUserById = useMutation(searchUserByCitizenId);
    const mutationAddMember = useMutation(addNewMemberByCompanyID);

    const closeFeedbackModal = () => setFeedbackModal(INITIAL_FEEDBACK_MODAL);

    const showInfoModal = (message, onConfirm) => {
        setFeedbackModal({ open: true, variant: "info", title: "Información", message, showCancel: false, onConfirm });
    };

    const showSuccessModal = (message, onConfirm) => {
        setFeedbackModal({ open: true, variant: "success", title: "¡Listo!", message, showCancel: false, onConfirm });
    };

    const handleCompanySelect = (company) => {
        setIsModalListMemberVisible(true);
    };

    const handleModalListMemberVisible = () => {
        setIsModalListMemberVisible(false);
    };

    const handleDeleteApplicantByCompany = (data) => {
        setFeedbackModal({
            open: true,
            variant: "danger",
            title: "¿Está seguro?",
            message: `Se eliminará al miembro ${data.citizen} de esta empresa. Esta acción no se puede deshacer.`,
            showCancel: true,
            confirmLabel: "Sí, eliminar",
            cancelLabel: "Cancelar",
            onConfirm: () => {
                mutationDeleteApplicant.mutate(
                    { member_rol: ROLE_TYPE.APPLICANT, citizen_id: data.citizen, company_id: selectedCompany?.id ?? companyID },
                    {
                        onSuccess: (data) => {
                            showInfoModal(data.msg);
                            if (data.success) {
                                queryClient.invalidateQueries("allMembersData");
                                queryClient.invalidateQueries("allMembers");
                            }
                        },
                    }
                );
            },
        });
    };

    const handleModalAddMemberVisible = () => {
        sendInviteFormik.resetForm();
        setSearchUser(undefined);
        setModalInviteVisible(!modalInviteVisible);
    };

    const handlePageChange = (value) => {
        setCurrentPage(value);
    };

    const sendInviteFormik = useFormik({
        initialValues: {
            citizen_id: "",
        },
        validationSchema: FormCompanySendInviteSchema,
        onSubmit: (values) => {
            searchUserById(values.citizen_id);
        },
    });

    const searchUserById = (formData) => {
        mutationSearchUserById.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    setSearchUser(data.payload);
                    queryClient.invalidateQueries("searchUserId");
                } else {
                    showInfoModal(data.msg);
                }
            },
        });
    };

    const deleteFoundUser = () => {
        setSearchUser(null);
        sendInviteFormik.resetForm();
    };

    const handleAddMember = () => {
        addMember({ citizen_id: searchUser.citizen_id, company_id: selectedCompany.id });
    };

    const addMember = (formData) => {
        mutationAddMember.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    showSuccessModal(data.msg || "El usuario ha sido vinculado exitosamente a la empresa.", () => {
                        queryClient.invalidateQueries("allMembersData");
                        queryClient.invalidateQueries("allMembers");
                        setSearchUser(undefined);
                        closeFeedbackModal();
                        handleModalAddMemberVisible();
                        sendInviteFormik.resetForm();
                    });
                } else {
                    showInfoModal(data.msg);
                }
            },
        });
    };

    return (
        <>
        <Dialog
            open={isModalListMembersVisible}
            onClose={handleModalListMemberVisible}
            TransitionComponent={Slide}
            disableEscapeKeyDown
            fullWidth
            maxWidth="lg"
            sx={{
                '& .MuiPaper-root': {
                    borderRadius: '24px',
                    padding: { xs: '20px', sm: '28px' },
                    boxShadow: 'none',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E1E9EC'
                }
            }}
        >
            <IconButton
                onClick={handleModalListMemberVisible}
                sx={{
                    position: "absolute",
                    right: 20,
                    top: 20,
                    bgcolor: "#F0F4FF",
                    color: "#0F2350",
                    transition: "all 0.2s",
                    "&:hover": {
                        bgcolor: "#EA361A",
                        color: "#FFFFFF"
                    }
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 1, border: 'none' }}>
                {detailCompanyActive && (
                    <Box sx={{ mb: 3 }}>
                        <DetailCompany selectedCompany={selectedCompany} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddIcon />}
                                onClick={handleModalAddMemberVisible}
                                sx={{
                                    bgcolor: "#0F2350",
                                    color: "#FFFFFF",
                                    borderRadius: "12px",
                                    px: 3,
                                    py: 1.2,
                                    fontFamily: "Roboto, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    textTransform: "none",
                                    boxShadow: "none",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        bgcolor: "#EA361A",
                                        boxShadow: "none",
                                    }
                                }}
                            >
                                Invitar Miembro
                            </Button>
                        </Box>
                    </Box>
                )}

                <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography
                        sx={{
                            fontFamily: 'Merriweather, Georgia, serif',
                            fontSize: '20px',
                            fontWeight: 800,
                            color: '#0F2350'
                        }}
                    >
                        Listado de Miembros
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: 'Roboto, sans-serif',
                            fontSize: '13.5px',
                            color: '#57697F'
                        }}
                    >
                        Personal y representantes vinculados a esta empresa
                    </Typography>
                </Box>

                <Table
                    rows={usersMembers?.payload ?? []}
                    columns={rolBase === ROLE_TYPE.COMPANY ? columnsCompanyApplicant : columns}
                    onSelect={handleCompanySelect}
                    onDelete={handleDeleteApplicantByCompany}
                    handlePageChange={handlePageChange}
                    currentPage={currentPage}
                    withoutRecord="En estos momentos no existen miembros asociados a esta empresa."
                />
                {dialogSelectActive && (
                    <DialogSelect
                        claimModalVisible={modalInviteVisible}
                        handleClaimModalVisibility={handleModalAddMemberVisible}
                        formik={sendInviteFormik}
                        searchUser={searchUser}
                        deleteFoundUser={deleteFoundUser}
                        onClick={handleAddMember}
                        title="Agregar miembro"
                        sendButtonLabel={"ENVIAR"}
                    />
                )}
            </DialogContent>
        </Dialog>

        <ConfirmActionModal
            open={feedbackModal.open}
            onClose={closeFeedbackModal}
            onConfirm={feedbackModal.onConfirm}
            variant={feedbackModal.variant}
            title={feedbackModal.title}
            message={feedbackModal.message}
            showCancel={feedbackModal.showCancel}
            confirmLabel={feedbackModal.confirmLabel}
            cancelLabel={feedbackModal.cancelLabel}
            loading={mutationDeleteApplicant.isLoading}
        />
        </>
    );
}
